import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDown, ChevronLeft, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CompanyView } from '../../../../types/entity-types';
import { getCountryFlag } from '../../../../utils/get-country-flag';
import { InstitutionListWrapper } from './components/institution-list-wrapper';

type OpenBankingPageProps = {
  companies: CompanyView[];
};

export const OpenBankingPage = ({ companies }: OpenBankingPageProps) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<CompanyView>(companies[0]);

  const onSelectCompany = (company: CompanyView) => {
    setSelectedCompany(company);
  };

  return (
    <div className="container mx-auto p-5">
      <div className="mb-4 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="rounded-full bg-seapoint-gray-medium p-3 hover:bg-seapoint-gray-dark"
        >
          <ChevronLeft />
        </button>

        <h1 className="text-xl font-medium">Connect Open Banking Account</h1>
      </div>

      <div className="mb-8 flex items-center gap-6">
        <div className="w-72">
          <h2 className="mb-4 text-xs font-semibold leading-5">Select Company</h2>

          <Menu as="div" className="relative">
            <MenuButton className="flex h-10 w-full items-center justify-between rounded-2xl bg-seapoint-gray-medium px-4 py-2 shadow-sm">
              <div className="flex items-center gap-3">
                {selectedCompany && (
                  <img
                    src={getCountryFlag(selectedCompany.legalEntity.country)}
                    alt=""
                    className="h-4 w-4 rounded-full object-cover"
                  />
                )}
                <span className="text-xs font-medium">{selectedCompany?.legalEntity.name || 'Select a company'}</span>
              </div>

              {/* TODO: show chevron down when open */}
              <ChevronDown size={16} />
            </MenuButton>

            <MenuItems className="absolute z-10 mt-2 w-full rounded-2xl bg-white shadow-lg">
              {companies.map((company) => (
                <MenuItem key={company.id}>
                  {({ active }) => (
                    <div
                      className={`flex cursor-pointer items-center gap-3 p-4 ${active ? 'bg-gray-50' : ''}`}
                      onClick={() => onSelectCompany(company)}
                    >
                      <img
                        src={getCountryFlag(company.legalEntity.country)}
                        alt=""
                        className="h-4 w-4 rounded-full object-cover"
                      />
                      <span className="text-xs font-medium">{company.legalEntity.name}</span>
                    </div>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>
        </div>

        <div className="flex-1">
          <h2 className="mb-4 text-xs font-semibold">Search Banks in {selectedCompany?.legalEntity.country}</h2>

          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search size={16} className="opacity-50" />
            </div>

            <input
              type="text"
              placeholder="Search institutions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 w-full rounded-3xl bg-seapoint-gray-medium py-2 pl-10 pr-4 placeholder:text-xs focus:outline-none focus:ring-2 focus:ring-seapoint-yellow"
            />
          </div>
        </div>
      </div>

      <InstitutionListWrapper searchQuery={searchQuery} company={selectedCompany} />
    </div>
  );
};
